import { RefObject, useLayoutEffect, useRef, useState } from 'react';

import { fitCount } from './fit';

// Room kept for the "+N more" control ("+12 more" at 16/700 plus padding).
const MORE_WIDTH = 96;
// The list's column gap (UI spec §3.3: `gap: 8px 16px`).
const COLUMN_GAP = 16;

/** Marks the list items that are entries (not the "+N more" control). */
export const FIT_ITEM_ATTR = 'data-fit-item';
/** Marks the "+N more" list item, measured while it is on screen. */
export const FIT_MORE_ATTR = 'data-fit-more';

const contentWidth = (el: HTMLElement): number => {
  const style = window.getComputedStyle(el);
  const padding =
    (parseFloat(style.paddingLeft) || 0) +
    (parseFloat(style.paddingRight) || 0);
  return el.clientWidth - padding;
};

/**
 * DB-05: how many of `count` entries fit in two wrapped rows of the
 * list. The list has its natural width and wraps under the label once it
 * does not fit beside it, so the room it can take is the bar's content
 * width: the list's parent, which does not change as entries are hidden.
 *
 * Entry widths are measured once with every entry rendered (`key` changes
 * when the entries or their on/off state change), then reused on every
 * resize, and measured again once the webfont has loaded. With no
 * ResizeObserver (jsdom) everything is shown.
 *
 * `settled` is false while a remeasure briefly shows every entry, so a
 * caller can tell "everything fits" from that passing state. `lastVisible`
 * is the count from the last finished measurement: during a remeasure a
 * caller can keep "+N more" (and its open list) mounted with it, so a poll
 * that only changes someone's status does not drop focus or replay the
 * list's pop-in. The passing state is never painted.
 */
export const useFitCount = (
  listRef: RefObject<HTMLElement>,
  count: number,
  key: string,
): { visible: number; settled: boolean; lastVisible: number } => {
  const [visible, setVisible] = useState(count);
  const [measured, setMeasured] = useState(false);
  // Bumped to force a fresh measurement (the webfont arrived).
  const [remeasure, setRemeasure] = useState(0);
  const widths = useRef<number[]>([]);
  // The "+N more" control's width as last rendered, else the MORE_WIDTH
  // guess. Room for it is kept on the last allowed row, so a guess
  // narrower than the control would push it onto a third row, and a wider
  // one would hide an entry for nothing. Follows the control both ways.
  const moreWidth = useRef(MORE_WIDTH);
  const lastVisible = useRef(count);

  useLayoutEffect(() => {
    widths.current = [];
    setVisible(count);
    setMeasured(false);

    // Widths taken in the fallback font are wrong once Manrope arrives.
    // Registered here, once per entry set, not in the measuring effect,
    // which re-runs on every `visible` change.
    let active = true;
    document.fonts?.ready?.then(() => {
      if (!active) return;
      widths.current = [];
      setRemeasure((n) => n + 1);
    });
    return () => {
      active = false;
    };
  }, [count, key]);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return undefined;
    const box = list.parentElement ?? list;
    const measure = () => {
      if (widths.current.length !== count) {
        const items = Array.from(
          list.querySelectorAll<HTMLElement>(`:scope > [${FIT_ITEM_ATTR}]`),
        );
        if (items.length < count) {
          // Some entries are collapsed: show them all, then measure again.
          setVisible(count);
          setMeasured(false);
          return;
        }
        widths.current = items.map(
          (item) => item.getBoundingClientRect().width,
        );
      }
      const more = list.querySelector<HTMLElement>(
        `:scope > [${FIT_MORE_ATTR}]`,
      );
      moreWidth.current = more
        ? more.getBoundingClientRect().width || MORE_WIDTH
        : MORE_WIDTH;
      lastVisible.current = fitCount(
        contentWidth(box),
        widths.current,
        moreWidth.current,
        { gap: COLUMN_GAP },
      );
      setVisible(lastVisible.current);
      setMeasured(true);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [listRef, count, key, visible, remeasure]);

  return {
    visible,
    settled: measured || typeof ResizeObserver === 'undefined',
    lastVisible: lastVisible.current,
  };
};
